import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Upload, Trash2, Eye, Download, FileText, Image as ImageIcon, FileType, Archive } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const getAuthToken = () => localStorage.getItem('valnexo_token') || '';

const MAX_MB = 25;
const humanSize = (b) => {
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
    return `${(b / 1024 / 1024).toFixed(1)} Mo`;
};

const iconFor = (ct) => {
    if (!ct) return <FileType className="w-4 h-4 text-slate-500" />;
    if (ct.startsWith('image/')) return <ImageIcon className="w-4 h-4 text-blue-500" />;
    if (ct === 'application/pdf') return <FileText className="w-4 h-4 text-rose-600" />;
    if (ct.includes('zip')) return <Archive className="w-4 h-4 text-amber-600" />;
    return <FileType className="w-4 h-4 text-slate-500" />;
};

const isPreviewable = (ct) => (ct || '').startsWith('image/') || ct === 'application/pdf';

/**
 * Onglet Documents du dialog Qualification (spec §22).
 * - Upload drag & drop (multi-fichiers) + bouton
 * - Aperçu inline images/PDF via œil
 * - Téléchargement individuel + téléchargement ZIP global
 * - Suppression soft avec confirmation
 * - RBAC : le backend gère les permissions ; on affiche/masque les boutons selon
 *   le contexte : admin/VP peut tout supprimer ; l'uploader peut supprimer les
 *   siens tant que la qualif n'est pas validée.
 * - Suggestions contextuelles au-dessus de la dropzone, différentes selon
 *   le formulaire long (technique) vs court (parrain).
 */
const SUGGESTED_DOCUMENTS = [
    'Facture d’électricité la plus récente (hiver)',
    'Facture d’électricité la plus récente (été)',
    'Facture de gaz la plus récente (hiver, si concerné)',
    'Facture de gaz la plus récente (été, si concerné)',
];

export default function ChantierQualificationDocumentsTab({ qualification, currentUser, canEdit }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);      // {doc, blobUrl}
    const [confirmDelete, setConfirmDelete] = useState(null); // doc à supprimer
    const inputRef = useRef(null);
    const qualId = qualification?.id;

    const load = useCallback(async () => {
        if (!qualId) return;
        setLoading(true);
        try {
            const r = await api.get(`/chantier-qualifications/${qualId}/documents`);
            setDocs(r.data || []);
        } catch {
            setDocs([]);
        } finally { setLoading(false); }
    }, [qualId]);

    useEffect(() => { load(); }, [load]);

    const uploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        let okCount = 0, ko = [];
        for (const file of files) {
            if (file.size > MAX_MB * 1024 * 1024) {
                ko.push(`${file.name} (>${MAX_MB} Mo)`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append('file', file);
                await api.post(`/chantier-qualifications/${qualId}/documents`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                okCount++;
            } catch (e) {
                ko.push(`${file.name} (${e.response?.data?.detail || 'erreur'})`);
            }
        }
        setUploading(false);
        if (okCount) toast.success(`${okCount} fichier${okCount > 1 ? 's' : ''} ajouté${okCount > 1 ? 's' : ''}`);
        if (ko.length) toast.error(`Refusés : ${ko.join(', ')}`);
        load();
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        uploadFiles(Array.from(e.dataTransfer.files || []));
    };

    const openPreview = async (doc) => {
        try {
            const token = getAuthToken();
            const r = await api.get(`/chantier-qualifications/${qualId}/documents/${doc.id}`, {
                responseType: 'blob',
                params: { auth: token },
            });
            const blobUrl = URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
            setPreviewDoc({ doc, blobUrl });
        } catch {
            toast.error('Impossible d\'ouvrir l\'aperçu');
        }
    };

    const doDownload = (doc) => {
        const token = getAuthToken();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/chantier-qualifications/${qualId}/documents/${doc.id}?download=1&auth=${encodeURIComponent(token)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const doDownloadAllZip = () => {
        const token = getAuthToken();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/chantier-qualifications/${qualId}/documents-zip?auth=${encodeURIComponent(token)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/chantier-qualifications/${qualId}/documents/${confirmDelete.id}`);
            toast.success('Document supprimé');
            setConfirmDelete(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Suppression impossible');
        }
    };

    const canDelete = (d) => {
        const role = currentUser?.role;
        if (role === 'admin_full' || role === 'admin' || role === 'vp' || currentUser?.is_admin) return true;
        // Uploader propre + qualif non validée
        return d.uploaded_by_user_id === currentUser?.id && qualification?.status !== 'validated';
    };

    return (
        <div className="space-y-4" data-testid="qualif-panel-documents">
            {/* Suggestions contextuelles */}
            {canEdit && (
                <div className="rounded-md border border-blue-200 bg-blue-50/60 px-4 py-3" data-testid="qualif-docs-suggestions">
                    <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-1.5">
                        Documents utiles à ajouter (facultatif)
                    </p>
                    <ul className="text-xs text-slate-700 space-y-0.5 pl-3 list-disc marker:text-blue-500">
                        {SUGGESTED_DOCUMENTS.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                </div>
            )}

            {/* Zone drop & upload */}
            {canEdit && (
                <div
                    onDrop={onDrop}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50/50'}`}
                    data-testid="qualif-docs-dropzone"
                >
                    <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-700 font-medium">Glissez vos fichiers ici</p>
                    <p className="text-xs text-slate-500 mt-1">Images, PDF, Excel, Word, ZIP · max {MAX_MB} Mo par fichier</p>
                    <Button
                        variant="outline" size="sm" className="mt-3"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        data-testid="qualif-docs-upload-btn"
                    >
                        {uploading ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Envoi…</>) : 'Choisir des fichiers'}
                    </Button>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
                        data-testid="qualif-docs-file-input"
                    />
                </div>
            )}

            {/* Liste + ZIP */}
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-slate-700">
                    {docs.length} document{docs.length > 1 ? 's' : ''}
                </h4>
                {docs.length > 0 && (
                    <Button
                        variant="outline" size="sm" onClick={doDownloadAllZip}
                        data-testid="qualif-docs-zip-btn"
                    >
                        <Archive className="w-3.5 h-3.5 mr-1.5" /> Télécharger tout (ZIP)
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                </div>
            ) : docs.length === 0 ? (
                <p className="text-sm text-slate-500 italic text-center py-4">
                    Aucun document pour cette qualification pour le moment.
                </p>
            ) : (
                <div className="space-y-1.5">
                    {docs.map((d) => (
                        <div key={d.id}
                             className="flex items-center gap-3 py-2 px-3 border border-slate-200 rounded-md bg-white hover:bg-slate-50 group"
                             data-testid={`qualif-doc-row-${d.id}`}>
                            {iconFor(d.content_type)}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-800 truncate">{d.filename}</p>
                                <p className="text-xs text-slate-500">
                                    {humanSize(d.size)} · {new Date(d.uploaded_at).toLocaleString('fr-FR')}
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                {isPreviewable(d.content_type) && (
                                    <Button variant="ghost" size="icon" onClick={() => openPreview(d)}
                                            title="Aperçu"
                                            data-testid={`qualif-doc-preview-${d.id}`}>
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                )}
                                <Button variant="ghost" size="icon" onClick={() => doDownload(d)}
                                        title="Télécharger"
                                        data-testid={`qualif-doc-download-${d.id}`}>
                                    <Download className="w-4 h-4" />
                                </Button>
                                {canEdit && canDelete(d) && (
                                    <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(d)}
                                            title="Supprimer"
                                            data-testid={`qualif-doc-delete-${d.id}`}>
                                        <Trash2 className="w-4 h-4 text-rose-600" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Preview modal */}
            <Dialog open={!!previewDoc} onOpenChange={(o) => {
                if (!o) {
                    if (previewDoc?.blobUrl) URL.revokeObjectURL(previewDoc.blobUrl);
                    setPreviewDoc(null);
                }
            }}>
                <DialogContent className="max-w-5xl h-[85vh] p-0 overflow-hidden flex flex-col"
                               data-testid="qualif-doc-preview-dialog">
                    <DialogHeader className="px-6 pt-4 pb-2 border-b">
                        <DialogTitle className="flex items-center gap-2 text-slate-900 text-base">
                            {previewDoc && iconFor(previewDoc.doc.content_type)}
                            {previewDoc?.doc.filename}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center">
                        {previewDoc?.doc.content_type.startsWith('image/') && (
                            <img src={previewDoc.blobUrl} alt={previewDoc.doc.filename}
                                 className="max-w-full max-h-full object-contain" />
                        )}
                        {previewDoc?.doc.content_type === 'application/pdf' && (
                            <iframe src={previewDoc.blobUrl} title={previewDoc.doc.filename}
                                    className="w-full h-full border-0" />
                        )}
                    </div>
                    <DialogFooter className="px-6 py-3 border-t">
                        <Button variant="outline" onClick={() => doDownload(previewDoc.doc)}>
                            <Download className="w-3.5 h-3.5 mr-1.5" /> Télécharger
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <DialogContent className="max-w-sm" data-testid="qualif-doc-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Supprimer ce document ?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-600">
                        « {confirmDelete?.filename} » sera retiré de la qualification.
                        Cette action est réversible côté administration.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
                        <Button variant="destructive" onClick={doDelete}
                                data-testid="qualif-doc-delete-confirm-btn">
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
